/**
 * Where a structure sits in the depth-refinement cycle — the state contract
 * shared by the server that drives it and the web app that badges it.
 *
 * `processStructure` cannot verify its own guesses on the first pass: the
 * measurement needs `scanProvenance.targets` to know where to look, and a
 * multi-room scan needs the registration transform fitted against the PROCESSED
 * walls. Both only exist once pass 1 has finished, so the order is forced —
 *
 *     pass 1 (draft) -> measure against the raw LiDAR -> pass 2 (final)
 *
 * — and that is what makes the stored structure a draft in between.
 *
 * Lives in shared-lib because the answer to "is this a draft?" is asked in two
 * places (the sweep, and the viewer's header badge) and two copies of a state
 * machine drift silently. See reference-docs/structure-processing/scan-provenance.md.
 */

/**
 * Lives at `structures/{id}.depthFinalization.state`.
 *
 * A structure with NO `depthFinalization` field is not in the cycle and never
 * was — every scan processed before this shipped, plus anything the eligibility
 * gate declined. Absence is a meaningful third case, not a synonym for
 * `pending`: it is what keeps the entire existing corpus out of the draft state
 * without a migration.
 */
const DEPTH_STATE = {
  /**
   * Stage 4, the fixture check: the depth service is asking a vision model which
   * plumbed and gas fixtures RoomPlan missed in each kitchen and bathroom
   * (`depthFinalization.inventory`). Comes before `pending`, and always moves on
   * to it — stage 5 never waits on stage 4 for good.
   */
  INVENTORYING: 'inventorying',
  /**
   * The fixture check's answers are stored; the structure has not been rebuilt with
   * them yet. Landing them is a reprocess in place, or onto a copy when someone has
   * edited the draft (`refinedAs`), after which the scan moves on to `pending`.
   */
  INVENTORIED: 'inventoried',
  /** Eligible; the measurement has not been dispatched yet. */
  PENDING: 'pending',
  /** The depth service is measuring this scan. */
  MEASURING: 'measuring',
  /** Rows are written; the finalize reprocess has not run yet. */
  MEASURED: 'measured',
  /** Pass 2 is running. */
  REPROCESSING: 'reprocessing',
  /** Pass 2 finished — what is stored is the refined structure. */
  FINAL: 'final',
  /**
   * The user edited this scan, so the refinement was written to a SEPARATE
   * structure in the same project rather than over their work. `refinedAs`
   * holds its id; the refined copy carries `refinedFrom` pointing back.
   *
   * Terminal for this scan, and deliberately not a failure: the measurement ran
   * and produced something, it just did not land here.
   */
  FORKED: 'forked',
  /** Will not be refined. `skipReason` says why. */
  SKIPPED: 'skipped',
};

/** Why a scan will not be refined. */
const DEPTH_SKIP_REASON = {
  /** No LiDAR sidecar, so there is nothing to measure against. */
  NO_LIDAR: 'no-lidar',
  /** The pipeline inferred nothing a measurement could settle. */
  NO_TARGETS: 'no-targets',
  /** Someone edited the draft. Their work wins. */
  EDITED: 'edited',
  /** The measurement or the finalize reprocess kept failing. */
  FAILED: 'failed',
  /**
   * Rebuilt outside a cycle, from the scan alone: the measured rows standing
   * on it are applied only in a cycle's LiDAR stage, so the structure now
   * showing is not the refined one. Replaces a `final` stamp, which would
   * otherwise go on claiming it is; `finalizedAt` survives the merge.
   */
  NOT_APPLIED: 'not-applied',
  /**
   * The run ASKED for the cycle ("Reprocess with LiDAR Refinement") and this
   * environment has no measurement endpoint to run it against. Written rather
   * than nothing because the run was a request: absent would read "never
   * queued" with nothing to say why, under a reprocess message that had just
   * promised a refined version.
   */
  UNAVAILABLE: 'unavailable',
};

/**
 * Is this field of an `originalScans` doc a measured depth row set? Every
 * pass's field carries the `depth` prefix, and `scanPlanes` — the raw plane
 * set the depth service publishes — is the one that does not.
 */
function isDepthRowField(key) {
  return key === 'scanPlanes' || /^depth[A-Z]/.test(String(key));
}

/**
 * The states in which what's stored is a DRAFT — the pipeline's unverified first
 * pass, with a refined version still on its way.
 *
 * `final` and `skipped` are both terminal and neither is a draft: `final` has
 * been refined, and `skipped` never will be, so badging it would leave a
 * permanent "draft" on a scan nothing is going to replace.
 */
const DEPTH_DRAFT_STATES = [
  DEPTH_STATE.INVENTORYING,
  DEPTH_STATE.INVENTORIED,
  DEPTH_STATE.PENDING,
  DEPTH_STATE.MEASURING,
  DEPTH_STATE.MEASURED,
  DEPTH_STATE.REPROCESSING,
];

/**
 * Is what's stored a draft awaiting refinement?
 *
 * @param {Object} [finalization] - `structures/{id}.depthFinalization`
 * @returns {boolean}
 */
function isDepthDraft(finalization) {
  return DEPTH_DRAFT_STATES.includes(finalization?.state);
}

/**
 * The refinement finished and produced a separate structure, because this one
 * had been edited. `refinedAs` is where it went.
 *
 * @param {Object} [finalization]
 * @returns {string|null} the refined structure's id, or null
 */
function refinedElsewhereId(finalization) {
  if (finalization?.state !== DEPTH_STATE.FORKED) return null;
  return finalization.refinedAs || null;
}

/**
 * The cycle completed and the LiDAR changed nothing.
 *
 * Distinct from "refined": the user is told a measurement ran and agreed with
 * the pipeline, which is a real answer and not the same as silence.
 *
 * @param {Object} [finalization]
 * @returns {boolean}
 */
function isDepthNoChange(finalization) {
  return (
    finalization?.state === DEPTH_STATE.FINAL &&
    finalization?.noChanges === true
  );
}

module.exports = {
  DEPTH_STATE,
  refinedElsewhereId,
  isDepthNoChange,
  DEPTH_SKIP_REASON,
  DEPTH_DRAFT_STATES,
  isDepthDraft,
  isDepthRowField,
};
